import React, {useEffect, useRef, useState} from "react";
import {useResources} from "../../../stores/ResourceProvider";
import {
  Box,
  CircularProgress,
  FormLabel,
  Paper,
  TableContainer,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import Model from "../../../model/Model";
import Plan from "../../../model/Plan";
import {FutureCalc, IMonteCarloResults} from "../../../components/calculator/Calculator";
import FutureViewTable, {IFutureViewSettings} from "./charts/FutureViewTable";
import FutureViewChart from "./charts/FutureViewChart";
import BudgetProjectionChart from "./charts/BudgetProjectionChart";
import BudgetProjectionTable from "./charts/BudgetProjectionTable";
import BucketsChart from "./charts/BucketsChart";
import ViewMenu from "../../../components/controls/ViewMenu";
import WithdrawalProjectionChart from "./charts/WithdrawalProjectionChart";
import NetWorthProjectionChart from "./charts/NetWorthProjectionChart";
import NetWorthProjectionTable from "./charts/NetWorthProjectionTable";
import TaxDisclaimer from "../../../components/page/TaxDisclaimer";
import Methodology from "../../../components/page/Methodology";
import RmdDisclaimer from "../../../components/page/RmdDisclaimer";
import Tracking from "../../../components/Tracking";
import ScenarioComparison from "./charts/ScenarioComparison";
import ScenarioComparisonSettingsDialog from "./ScenarioComparisonSettingsDialog";
import UserSetting, {UserSettingType} from "../../../model/UserSetting";
import {useReactToPrint} from "react-to-print";
import PrintReport from "../../../components/page/PrintReport";
import {dateToLocalFormat} from "../../../stores/StoreUtilities";
import SocialSecurityBenefitChart from "./charts/SocialSecurityBenefitChart";
import SocialSecurityBenefitTable from "./charts/SocialSecurityBenefitTable";
import IModelItem from "../../../model/IModelItem";
import MonteCarloChart from "./charts/MonteCarloChart";
import MonteCarloTable from "./charts/MonteCarloTable";
import MonteCarloDialog from "./MonteCarloDialog";
import useIsMobile from "../../../components/useIsMobile";
import MobileDrawerButton from "../../../components/controls/MobileDrawerButton";
import SpendToTheEndView from "./charts/SpendToTheEndView";

export enum OptimizerChartView {
  NetLiquidAssets,
  NetWorthProjection,
  BudgetProjection,
  Buckets,
  WithdrawalProjection,
  SocialSecurity,
  MonteCarlo,
  SpendToTheEnd,
  ScenarioComparison,
}


export interface IOptimizerViewSettings {
  chartView: OptimizerChartView
  futureViewSettings: IFutureViewSettings
}

const OptimizerView = ({
  model,
  plan,
  updatedAt,
  width,
  onChange,
  toggleDrawer
} : {
  model: Model
  plan: Plan
  updatedAt: string
  width: number
  onChange?(item: IModelItem): void
  toggleDrawer: () => void
}) => {
  const isMobile = useIsMobile()
  const [viewStorageKey, setViewStorageKey] = useState<string>("")
  const [currentModel, setCurrentModel] = useState<Model | undefined>()
  const [currentPlan, setCurrentPlan] = useState<Plan | undefined>()
  const [chartView, setChartView] = useState<OptimizerChartView>(OptimizerChartView.NetLiquidAssets)
  const [futureViewSettings, setFutureViewSettings] = useState<IFutureViewSettings | undefined>()
  const [currentYear, setCurrentYear] = useState<number>(0)
  const [futureCalcs, setFutureCalcs] = useState<FutureCalc[]>([])
  const [scenarioComparisonUserSetting, setScenarioComparisonUserSetting] = useState<UserSetting | undefined>()
  const [showScenarioComparisonSettings, setShowScenarioComparisonSettings] = useState<boolean>(false)
  const [monteCarloResults, setMonteCarloResults] = useState<IMonteCarloResults | undefined>()
  const [showMonteCarloSettings, setShowMonteCarloSettings] = useState<boolean>(false)
  const [isProcessing , setIsProcessing] = useState<boolean>(false)

  const [isPrinting, setIsPrinting] = useState<boolean>(false)
  const promiseResolveRef = useRef<any>(null)
  useEffect(() => {
    if (isPrinting && promiseResolveRef.current) {
      promiseResolveRef.current();
    }
  }, [isPrinting]);

  const theme = useTheme()
  const styles = {
    formLabel: {
        fontSize: 12,
        fontWeight: 400,
        color: theme.palette.grey["700"],
    },
    miniButton: {
      height: 20,
      width: 40,
      borderRadius: "5px"
    },
    progress: {
      display: "flex",
      flexDirection: "column",
      flexGrow: 1,
      width: "100%",
      justifyContent: "center",
      alignItems: "center",
      marginTop: "230px",
      marginLeft: "auto",
      marginRight: "auto",
    }
  }

  const { modelStore, userStore, calculator } = useResources()

  const isAdvisorOrAdmin = userStore.isAdvisorOrAdmin
  const printRef = useRef(null)
  const xlUp = useMediaQuery(theme.breakpoints.up('xl'))

  useEffect(() => {
    // Scroll to top
    setTimeout(() => {
      window.scrollTo(0, 0)
    }, 200);
  }, [])

  useEffect(() => {
    setCurrentModel(model)
    setCurrentPlan(plan)
    loadSettings()
    // console.log(`OptimizerView Plan updatedAt: ${updatedAt}`)
  }, [model, plan, updatedAt, isMobile])

  useEffect(() => {
    saveSettings()
  }, [chartView, futureViewSettings, viewStorageKey])

  useEffect(() => {
    // Spend to the End runs its own calc against a cloned model — skip the
    // global calc on that tab so its FutureCalc doesn't get overwritten and
    // unnecessary work isn't done.
    if (chartView === OptimizerChartView.SpendToTheEnd) return
    calculateFuture()
  },[currentModel, currentModel?.updatedAt, currentPlan, currentPlan?.updatedAt, chartView, updatedAt])

  const loadSettings = () => {
    let storageKey = viewStorageKey
    if (!storageKey) {
      storageKey = `${modelStore.currentUser?.id}.OptimizerViewSettings`
      setViewStorageKey(storageKey)
    }
    const data = window.localStorage.getItem(storageKey)
    if (data) {
      const settings: IOptimizerViewSettings = JSON.parse(data)
      // Bounds-check stored chartView so an inserted enum value (e.g. SpendToTheEnd
      // shifting ScenarioComparison's index) doesn't drop the user onto the wrong tab.
      const view = settings.chartView
      const isValid = typeof view === "number"
        && view >= 0
        && ((isAdvisorOrAdmin && view <= OptimizerChartView.ScenarioComparison) ||
           (!isAdvisorOrAdmin && view <= OptimizerChartView.MonteCarlo))
      setChartView(isValid ? view : OptimizerChartView.NetLiquidAssets)

      if (settings.futureViewSettings) {
        setFutureViewSettings(settings.futureViewSettings)
      }
    }

    if (isMobile) {
      setChartView(OptimizerChartView.NetLiquidAssets)
    }
  }

  const saveSettings = () => {
      const settings = JSON.stringify({
          chartView: chartView,
          futureViewSettings: futureViewSettings
      })
      window.localStorage.setItem(viewStorageKey, settings)
  }

  const calculateFuture = async () => {
    if (!currentModel || !currentPlan) {
      return
    }

    const calculateMonteCarlo = async () => {
      console.log(`MonteCarlo: calculating`)
      const start = new Date()
      // console.profile("MonteCarlo")
      const results = await calculator.calculateMonteCarlo(model, plan, maxEndYear)
      // console.profileEnd("MonteCarlo")
      setMonteCarloResults(results)
      setIsProcessing(false)
      const end = new Date()
      console.log(`MonteCarlo took ${(end.getTime() - start.getTime())/1000} seconds`)
    }

    const plan = currentPlan

    let calcs: FutureCalc[] = []

    let maxEndYear: number = 0
    const latestLifeExpectancyDate = currentModel.getPlanEndDate(plan)
    if (latestLifeExpectancyDate) {
      const latestLifeExpectancyYear = latestLifeExpectancyDate.getFullYear()
      maxEndYear = latestLifeExpectancyYear
    }

    // console.debug(`OptimizerView.calculateFuture to ${maxEndYear}`)
    const multiModel = false

    if (chartView === OptimizerChartView.MonteCarlo) {
      if ((!monteCarloResults || monteCarloResults.updatedAt !== updatedAt)  && !isProcessing) {
        setIsProcessing(true)
        let timer = setTimeout(() => {
          calculateMonteCarlo()
          clearTimeout(timer)
        }, 200)
      }
    } else {
      const label = multiModel ? `${currentModel.name} - ${plan.name}` : plan.name
      // console.profile("OptimizerView.calculateFuture")
      const calc = calculator.calculateFuture(currentModel, label, maxEndYear)
      // console.profileEnd("OptimizerView.calculateFuture")
      calcs.push(calc)
    }

    setFutureCalcs(calcs)

    if (modelStore.currentUser && isAdvisorOrAdmin) {
      let userSetting = modelStore.currentUser.settings.find((setting: UserSetting) => setting.settingType === UserSettingType.scenarioComparison)
      if (!userSetting) {
        // Default to the current scenario
        if (currentModel && currentPlan) {
          userSetting = new UserSetting({
            name: "Default",
            settings: JSON.stringify({
              scenarios: [
                {modelId: currentModel.id, planId: currentPlan.id}
              ]
            })
          })
        }
      }
      setScenarioComparisonUserSetting(userSetting)
    }
  }

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    onBeforeGetContent: () => {
      return new Promise((resolve) => {
        promiseResolveRef.current = resolve;
        setIsPrinting(true)
      })
    },
    onAfterPrint: () => {
      promiseResolveRef.current = null;
      setIsPrinting(false)
    }
  })

  const handleItemChange = (item: IModelItem) => {
    onChange?.(item)
  }

  const handleRefresh = async () => {
    // TODO: Keep from updating updatedBy
    // const update = await modelStore.saveSettings(model)
    const update = modelStore.touchModel(model)
    if (update && onChange) {
      // setCurrentModel(update)
      onChange(update)
    }
    // if (monteCarloResults) {
    //   setMonteCarloResults({...monteCarloResults, updatedAt: getISODateTime()})
    // }
    // // Send a fake IModelItem to force a refresh
    // onChange?.({
    //   id: currentModel!.id,
    //   createdAt: currentModel!.createdAt,
    //   updatedAt: getISODateTime(),
    //   accountId: currentModel!.accountId,
    //   userId: currentModel!.userId,
    //   modelId: currentModel!.id,
    //   description: "refresh",
    //   getTypename: () => "Refresh"
    // })
  }

  const renderViewMenu = () => {
    const options = ["Future View", "Net Worth Projection", "Budget Projection", "Buckets Strategy", "Withdrawal Projection", "Social Security", "Monte Carlo"]
    if (isAdvisorOrAdmin) {
      options.push("Spend to the End")
      options.push("Scenario Comparison")
    }
    let onPrint
    if (chartView === OptimizerChartView.NetLiquidAssets) {
      onPrint = (view: number) => {
        handlePrint()
      }
    }
    let onSettings
    if (chartView === OptimizerChartView.ScenarioComparison) {
      onSettings = (view: number) => {
        setShowScenarioComparisonSettings(true)
      }
    }

    const menuSize = xlUp && !isAdvisorOrAdmin ? "medium" : "small"

    return (
      <ViewMenu
        view={chartView}
        options={options}
        variant="auto"
        size={menuSize}
        onChange={(index: number) => {
          Tracking.event({action: "Optimizer View", label: options[index]})
          setChartView(index)
        }}
        onPrint={onPrint}
        onSettings={onSettings}
      />
    )
  }

  const getReportDetails = () => {
    const user = modelStore.currentUser
    if (user && currentModel) {
      const fullDate = dateToLocalFormat(new Date(), "MMM d, yyyy")
      return (
        <React.Fragment>
          <Box display="flex" alignItems="baseline">
            <Box width={50}><FormLabel sx={styles.formLabel}>Date:</FormLabel></Box>
            <Box><Typography variant="body2">{fullDate}</Typography></Box>
          </Box>
          <Box display="flex" alignItems="baseline">
            <Box width={50}><FormLabel sx={styles.formLabel}>Name:</FormLabel></Box>
            <Box><Typography variant="body2">{user!.fullName}</Typography></Box>
          </Box>
          <Box display="flex" alignItems="baseline">
            <Box width={50}><FormLabel sx={styles.formLabel}>Model:</FormLabel></Box>
            <Box><Typography variant="body2">{currentModel!.name} ({currentModel!.appliedPlan?.name})</Typography></Box>
          </Box>
        </React.Fragment>
      )
    } else {
      return null
    }
  }

  return(
    <Box sx={{
      display: "flex",
      flexGrow: 1,
      flexDirection: "column",
      width: isMobile ? width - 8 : width - 24,
      maxWidth: isMobile ? width - 8 : width - 24,
      overflowX:"scroll",
      marginTop:"9px",
    }}>
      <Box sx={{display:"flex", mt:0, pl:0, pb:0, mb:"2px", justifyContent:"stretch", alignItems:"center"}}>
        {isMobile && <MobileDrawerButton onClick={toggleDrawer}/>}
        <Typography variant="h2" color="primary">Retirement Optimizer</Typography>
      </Box>
      {isMobile && futureCalcs.length > 0 &&
        <>
          <FutureViewChart year={currentYear} height={220} width={width - 16} futureCalcs={futureCalcs}/>
          <FutureViewTable year={currentYear} futureCalcs={futureCalcs}
                           settings={futureViewSettings}
                           isPrinting={isPrinting}
                           onChange={(settings: IFutureViewSettings) => setFutureViewSettings(settings)}
          />
        </>
      }
      {!isMobile &&
        <Box display="flex" flexDirection="column" alignContent="left" pl={1} height={50}>
          <FormLabel sx={styles.formLabel}>View</FormLabel>
          {renderViewMenu()}
        </Box>
      }
      {!isMobile &&
        <Box display="flex" flexDirection="column" flexGrow={1} pl={0} mb={1} mr={1} pr={0}>
          {chartView === OptimizerChartView.NetLiquidAssets && futureCalcs.length > 0 &&
            <Box ref={printRef}>
              <PrintReport isPrinting={isPrinting}
                           title="Future View Report"
                           details={getReportDetails()}
              >
                <FutureViewChart year={currentYear} futureCalcs={futureCalcs}/>
                <FutureViewTable year={currentYear} futureCalcs={futureCalcs}
                                 settings={futureViewSettings}
                                 isPrinting={isPrinting}
                                 onChange={(settings: IFutureViewSettings) => setFutureViewSettings(settings)}
                />
              </PrintReport>
            </Box>
          }
          {chartView === OptimizerChartView.MonteCarlo && !monteCarloResults &&
            <Box>
              <TableContainer component={Paper} sx={{ paddingTop:1, justifyContent:"center", height: 500, width:"100%" }}>
                <Box sx={styles.progress}>
                  <CircularProgress disableShrink={true}/>
                </Box>
              </TableContainer>
            </Box>
          }
          {chartView === OptimizerChartView.MonteCarlo && monteCarloResults &&
            <Box>
              <MonteCarloChart year={currentYear} monteCarloResults={monteCarloResults} isProcessing={isProcessing}
                               onSettings={() => setShowMonteCarloSettings(true)} onRefresh={handleRefresh}/>
              <MonteCarloTable year={currentYear} monteCarloResults={monteCarloResults}/>
            </Box>
          }
          {chartView === OptimizerChartView.NetWorthProjection && futureCalcs.length > 0 &&
            <React.Fragment>
              <NetWorthProjectionChart futureCalc={futureCalcs[0]}  year={currentYear}/>
              <NetWorthProjectionTable futureCalc={futureCalcs[0]}  year={currentYear}/>
            </React.Fragment>
          }
          {chartView === OptimizerChartView.BudgetProjection && futureCalcs.length > 0 &&
            <React.Fragment>
              <BudgetProjectionChart futureCalc={futureCalcs[0]} year={currentYear}/>
              <BudgetProjectionTable futureCalc={futureCalcs[0]} year={currentYear}/>
            </React.Fragment>
          }
          {chartView === OptimizerChartView.Buckets && futureCalcs.length > 0 &&
            <React.Fragment>
              <BucketsChart futureCalcs={futureCalcs} onChange={handleItemChange}/>
            </React.Fragment>
          }
          {chartView === OptimizerChartView.WithdrawalProjection && futureCalcs.length > 0 &&
            <React.Fragment>
              <WithdrawalProjectionChart futureCalc={futureCalcs[0]} year={currentYear}/>
              {/*<WithdrawalProjectionTable futureCalc={futureCalcs[0]} year={currentYear}/>*/}
            </React.Fragment>
          }
          {chartView === OptimizerChartView.SocialSecurity && futureCalcs.length > 0 &&
            <React.Fragment>
              <SocialSecurityBenefitChart futureCalc={futureCalcs[0]} year={currentYear}/>
              <SocialSecurityBenefitTable futureCalc={futureCalcs[0]} year={currentYear}/>
            </React.Fragment>
          }
          {chartView === OptimizerChartView.ScenarioComparison && futureCalcs.length > 0 &&
            <React.Fragment>
              <ScenarioComparison userSetting={scenarioComparisonUserSetting} futureCalc={futureCalcs[0]}/>
            </React.Fragment>
          }
          {chartView === OptimizerChartView.SpendToTheEnd && currentModel && currentPlan &&
            <SpendToTheEndView model={currentModel} plan={currentPlan} updatedAt={updatedAt}
                               onChange={handleItemChange}/>
          }
        </Box>
      }

      {showScenarioComparisonSettings &&
        <ScenarioComparisonSettingsDialog user={modelStore.currentUser!} userSetting={scenarioComparisonUserSetting}
                                  open={showScenarioComparisonSettings}
                                  onSave={(userSetting: UserSetting) => {
                                    setScenarioComparisonUserSetting(userSetting)
                                    setShowScenarioComparisonSettings(false)
                                  }}
                                  onClose={() => {
                                    setShowScenarioComparisonSettings(false)
                                  }}/>
      }
      {chartView !== OptimizerChartView.ScenarioComparison &&
        <Box sx={{display:"flex", flexDirection:"column", m:2}}>
          <Methodology/>
          <TaxDisclaimer/>
          <RmdDisclaimer/>
        </Box>
      }
      {showMonteCarloSettings &&
        <MonteCarloDialog plan={plan} model={model}
                          onClose={() => setShowMonteCarloSettings(false)}
                          onSave={(model: Model) => {
                            setShowMonteCarloSettings(false)
                            handleItemChange(model)
                          }}
        />
      }
    </Box>
  )
}


export default OptimizerView